import { useEffect, useState } from 'react';
import {
  Button,
  Typography,
  Link,
  Dialog,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { styled } from '@mui/material/styles';

import { ALL_ROUTES as WEBEX_ROUTES } from 'src/webex/routes';
import { useConsents } from 'src/contexts/ConsentsContext';
import {
  DEFAULT_COOKIES_CONSENT_TYPES,
  PRIVACY_POLICY_LINK,
} from 'src/constants';
import CookiesDetailedModal from './CookiesDetailedModal';

const linkProps = {
  target: '_blank',
  rel: 'noopener',
  // brandDarkBlue: link text needs >=4.5:1 on the #F0F0F0 dialog bg (WCAG 1.4.3)
  sx: { color: '#245284' },
};

const IGNORE_PATHS = [...WEBEX_ROUTES];

const ActionButton = styled(Button)(() => {
  return {
    height: 46,
    fontSize: 16,
    fontWeight: 700,
  };
});

const CookiesBasicConsent = () => {
  const [showBasicDialog, setShowBasicDialog] = useState(false);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);
  const consents = useConsents();

  useEffect(() => {
    const isIgnoredPath = IGNORE_PATHS.includes(window.location.pathname);
    if (isIgnoredPath) {
      return;
    }

    setShowBasicDialog(consents.missingCookiesConsent);
  }, [consents.missingCookiesConsent]);

  const handleCustomize = () => setShowDetailsDialog(true);

  const handleAccept = async () => {
    await consents.acceptCookiesPolicies(DEFAULT_COOKIES_CONSENT_TYPES);
  };

  const handleAcceptDetailed = async (types) => {
    await consents.acceptCookiesPolicies(types);
    setShowDetailsDialog(false);
  };

  return (
    <>
      {/*
        Modal dialog, not a floating overlay: a non-modal fixed card left the
        content behind it keyboard-reachable but visually covered, failing
        WCAG 2.2 SC 2.4.11 (Focus Not Obscured). As a modal, nothing behind it
        is focusable, and the notice is announced to assistive tech.
        No onClose/Esc: the notice records consent, so it must be acknowledged.
      */}
      <Dialog
        open={showBasicDialog}
        aria-labelledby="cookies-notice-title"
        disableEscapeKeyDown
        hideBackdrop
        disableScrollLock
        sx={{
          // Keep the original look: an undimmed card floating bottom-right,
          // not a centred modal. Only the semantics are modal — focus stays
          // inside and the page behind is hidden from assistive tech, which is
          // what makes 2.4.11 hold.
          '& .MuiDialog-container': {
            alignItems: 'flex-end',
            justifyContent: 'flex-end',
          },
          '& .MuiDialog-paper': {
            m: 2,
            width: 500,
            maxWidth: 'calc(100% - 32px)',
            borderRadius: '8px',
            backgroundColor: '#F0F0F0',
            boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
          },
        }}
      >
        <DialogContent>
          <Typography variant="h3" id="cookies-notice-title" mb={1}>
            Essential Technologies Notice
          </Typography>
          <Typography variant="t3_light" mb={1} lineHeight={'unset'}>
            We use only essential (strictly necessary) cookies, tokens, and
            secure local storage or similar technologies to operate our site and
            services. These technologies help us keep you signed in, maintain
            site security, and recognize returning users. We do not use
            analytics tools, advertising trackers, pixel tags, social media
            plugins, device fingerprinting, or any other technologies that track
            your behavior across sites or devices. By using our site, you agree
            to the use of these essential technologies. Learn more in our{' '}
            <Link {...linkProps} href={PRIVACY_POLICY_LINK}>
              Privacy Policy
            </Link>
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'center', pb: 2 }}>
          <ActionButton
            variant="contained"
            onClick={handleAccept}
            sx={{ width: 120 }}
          >
            Ok
          </ActionButton>
        </DialogActions>
      </Dialog>
      <CookiesDetailedModal
        open={showDetailsDialog}
        onClose={() => setShowDetailsDialog(false)}
        onAccept={handleAcceptDetailed}
      />
    </>
  );
};

export default CookiesBasicConsent;
